import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Users, UserPlus, Crown, Radio, Crosshair, Film, FileText, ChevronRight, Monitor, Loader2, Rocket } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Loader } from "@/components/common/Loader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { hasCap } from "@/lib/roles";
import { api } from "@/lib/api";

function PlatformSettings() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get("/admin/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

    const save = async (patch) => {
        const next = { ...cfg, ...patch };
        setCfg(next);
        setSaving(true);
        try {
            const { data } = await api.put("/admin/config", next);
            setCfg(data);
            toast.success("Platform settings saved.");
        } catch { toast.error("Could not save settings."); }
        finally { setSaving(false); }
    };

    if (!cfg) return null;
    return (
        <div className="bg-surface border border-border rounded-sm p-6 mb-6" data-testid="platform-settings">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Monitor className="w-4 h-4 text-volt" /> Platform Settings
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            </h3>
            <div className="flex items-center justify-between gap-4 py-2">
                <div>
                    <div className="text-sm text-white">Desktop App Banner</div>
                    <div className="text-xs text-muted-foreground">Show a "Get the Desktop App" promo on Live Capture for browser users.</div>
                </div>
                <Switch checked={!!cfg.desktop_banner_enabled} data-testid="toggle-desktop-banner"
                    onCheckedChange={(v) => save({ desktop_banner_enabled: v })}
                    className="data-[state=checked]:bg-volt" />
            </div>
            <div className="pt-3 border-t border-border/60 mt-2">
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Desktop App Download URL</label>
                <div className="flex gap-2 mt-1.5">
                    <input value={cfg.desktop_download_url || ""} data-testid="desktop-download-url"
                        onChange={(e) => setCfg({ ...cfg, desktop_download_url: e.target.value })}
                        placeholder="https://…/WhiteWater-AI-Setup.exe"
                        className="flex-1 h-10 bg-obsidian border border-border rounded-sm px-3 text-sm text-white" />
                    <button onClick={() => save({ desktop_download_url: cfg.desktop_download_url || "" })} data-testid="save-download-url"
                        className="h-10 px-4 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] text-sm">Save</button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Leave blank to show "Coming soon" on the banner.</p>
            </div>

            <div className="pt-4 border-t border-border/60 mt-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Rocket className="w-4 h-4 text-volt" /> Desktop Releases (Auto-Update)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Latest Version</label>
                        <input value={cfg.desktop_version || ""} data-testid="desktop-version"
                            onChange={(e) => setCfg({ ...cfg, desktop_version: e.target.value })} placeholder="1.0.0"
                            className="w-full h-10 bg-obsidian border border-border rounded-sm px-3 text-sm text-white mt-1.5 font-mono" />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Update Feed URL</label>
                        <input value={cfg.desktop_feed_url || ""} data-testid="desktop-feed-url"
                            onChange={(e) => setCfg({ ...cfg, desktop_feed_url: e.target.value })} placeholder="https://dl.…/desktop/"
                            className="w-full h-10 bg-obsidian border border-border rounded-sm px-3 text-sm text-white mt-1.5" />
                    </div>
                </div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mt-3">Release Notes</label>
                <textarea value={cfg.desktop_release_notes || ""} data-testid="desktop-release-notes"
                    onChange={(e) => setCfg({ ...cfg, desktop_release_notes: e.target.value })} rows={2}
                    placeholder="What's new in this version…"
                    className="w-full bg-obsidian border border-border rounded-sm px-3 py-2 text-sm text-white mt-1.5 resize-none" />
                <button onClick={() => save({ desktop_version: cfg.desktop_version, desktop_feed_url: cfg.desktop_feed_url, desktop_release_notes: cfg.desktop_release_notes })}
                    data-testid="save-release" className="mt-3 h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] text-sm">Save Release</button>
                <p className="text-[11px] text-muted-foreground mt-1.5">The Feed URL is where your <span className="font-mono">latest.yml</span> + installer are hosted (from <span className="font-mono">yarn build:win</span>). The desktop app checks it on launch and auto-updates.</p>
            </div>
        </div>
    );
}

export default function AdminOverview() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/stats").then(({ data }) => setData(data)); }, []);
    if (!data) return <Loader label="Loading platform analytics" />;

    const t = data.totals;
    const tip = { background: "#1A1A1A", border: "1px solid #333", borderRadius: 2, fontSize: 12 };

    return (
        <div className="ww-fade-up">
            <PageHeader title="Platform Overview" subtitle="Live analytics across all WhiteWater AI users." />

            {hasCap(user, "config") && <PlatformSettings />}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard testid="admin-stat-users" label="Total Users" value={t.users} sub={`+${t.new_7d} this week`} icon={Users} accent="#00C2FF" />
                <StatCard testid="admin-stat-new" label="New (30d)" value={t.new_30d} icon={UserPlus} accent="#00E676" />
                <StatCard testid="admin-stat-pro" label="Pro Members" value={t.pro} sub={`${t.free} free`} icon={Crown} accent="#FFB000" />
                <StatCard testid="admin-stat-monitoring" label="Live Now" value={t.monitoring_active} sub="monitoring" icon={Radio} accent="#FF3366" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard testid="admin-stat-matches" label="Matches" value={t.matches} icon={Crosshair} accent="#00C2FF" />
                <StatCard testid="admin-stat-clips" label="Clips" value={t.clips} icon={Film} accent="#00E676" />
                <StatCard testid="admin-stat-reports" label="AI Reports" value={t.reports} icon={FileText} accent="#FFB000" />
                <StatCard testid="admin-stat-coach" label="Coach Debriefs" value={t.coach_sessions} icon={FileText} accent="#FF3366" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 bg-surface border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold mb-4">Signups (Last 30 Days)</h3>
                    <div className="h-[260px] w-full min-w-0">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={260}>
                            <LineChart data={data.signups_trend}>
                                <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} minTickGap={24} />
                                <YAxis allowDecimals={false} tick={{ fill: "#666", fontSize: 10 }} width={28} />
                                <Tooltip contentStyle={tip} />
                                <Line type="monotone" dataKey="signups" stroke="#00C2FF" strokeWidth={2.5} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="bg-surface border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold mb-4">Plan Mix</h3>
                    <div className="h-[200px] w-full min-w-0">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                            <PieChart>
                                <Pie data={data.users_by_plan} dataKey="count" nameKey="plan" innerRadius={50} outerRadius={80} paddingAngle={3}>
                                    {data.users_by_plan.map((p, i) => <Cell key={i} fill={p.color} />)}
                                </Pie>
                                <Tooltip contentStyle={tip} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-4 mt-2">
                        {data.users_by_plan.map((p) => (
                            <div key={p.plan} className="flex items-center gap-1.5 text-xs">
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
                                <span className="text-muted-foreground">{p.plan}</span>
                                <span className="font-mono text-white">{p.count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-surface border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold mb-4">Matches by Game</h3>
                    <div className="h-[240px] w-full min-w-0">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
                            <BarChart data={data.matches_by_game}>
                                <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="game" tick={{ fill: "#A3A3A3", fontSize: 11 }} />
                                <YAxis allowDecimals={false} tick={{ fill: "#666", fontSize: 10 }} width={28} />
                                <Tooltip contentStyle={tip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                                <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                                    {data.matches_by_game.map((g, i) => <Cell key={i} fill={g.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="lg:col-span-2 bg-surface border border-border rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-bold">Recent Signups</h3>
                        <Link to="/admin/users" className="text-xs text-success hover:underline flex items-center gap-1" data-testid="view-all-users">All users <ChevronRight className="w-3 h-3" /></Link>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="text-left font-semibold py-2">User</th>
                                <th className="text-left font-semibold py-2">Plan</th>
                                <th className="text-left font-semibold py-2 hidden sm:table-cell">Role</th>
                                <th className="text-right font-semibold py-2">Joined</th>
                            </tr></thead>
                            <tbody>
                                {data.recent_users.map((u) => (
                                    <tr key={u.id} className="border-b border-border/50" data-testid={`recent-user-${u.id}`}>
                                        <td className="py-2.5"><div className="text-white">{u.name}</div><div className="text-xs text-muted-foreground font-mono">{u.email}</div></td>
                                        <td className="py-2.5"><StatusBadge status={u.plan === "pro" ? "Pro" : "Free"} /></td>
                                        <td className="py-2.5 hidden sm:table-cell text-muted-foreground capitalize">{u.role}</td>
                                        <td className="py-2.5 text-right text-muted-foreground font-mono text-xs">{String(u.created_at).slice(0, 10)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
